import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

import {
  ArrowLeftRight,
  ArrowRight,
  AlertTriangle,
  CircleCheckBig,
  Columns3,
  Info,
  List,
  Users,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import {
  PfmConfirmModal,
  default as RelationshipPrioritizedView,
} from "./RelationshipPrioritizedView";
import WorkingSetBanner from "./WorkingSetBanner";
import DropdownSwitcher from "../../Components/DropdownSwitcher";
import CommonModal from "../../Components/Modal";
import SearchInput from "../../Components/Search/SearchInput";
import TabsComponent from "../../Components/Tab";
import { useAppDispatch } from "../../Store/Hooks";
import type {
  LowScoreCriteria,
  PortfolioAccountRow,
  RelationshipPairRow,
} from "../../Store/Reducers/MyPortfolioOverviewSlice";
import { fetchMyPortfolioOverview } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import { isRole, ROLES } from "../../Utils/Constants/RoleConstants";
import { getInitials } from "../../Utils/Helper/Helper";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import {
  DEFAULT_LOW_SCORE_CRITERIA,
  normalizeLowScoreCriteria,
  parseLowScoreCriteria,
} from "../../Utils/LowScoreCriteria";
import { RelationshipProviders } from "../Relationship/account-relationships/RelationshipContexts";
import {
  ACCOUNT_IDENTITY_FALLBACK,
  getAccountIdentityBadgeStyle,
} from "../Relationship/account-relationships/relationshipUtils";
import { RlAssessmentModal } from "../Relationship/account-relationships/rl-assessment-modal";
import "../Relationship/account-relationships/relationship-prototype-skin.css";

type RelTab = "attn" | "healthy";
type Scope = "mine" | "team";
type AttnLayout = "columns" | "rows";

type AttentionBucket = "unassessed" | "no_goal_set" | "low_score";
type AttnFilter = "all" | AttentionBucket;

const ATTENTION_BUCKETS: AttentionBucket[] = [
  "unassessed",
  "no_goal_set",
  "low_score",
];

/** Shown when deep-linking from Account Priorities → Pairs for one account. */
type PairsFocusModalKind = "no_mappings" | "all_stable";

type RlStrengthBand = "strong" | "moderate" | "poor";

const RL_STRENGTH_META: Record<
  RlStrengthBand,
  { label: string; range: string; chipClass: string }
> = {
  strong: {
    label: "Strong",
    range: "RL 4–5",
    chipClass: "bg-emerald-50 text-emerald-700",
  },
  moderate: {
    label: "Moderate",
    range: "RL 2–3",
    chipClass: "border border-slate-200 bg-slate-50 text-slate-600",
  },
  poor: {
    label: "Weak",
    range: "RL 0–1",
    chipClass: "bg-amber-50 text-amber-700",
  },
};

const getRlStrengthBand = (
  rl: number | null | undefined
): RlStrengthBand | null => {
  if (rl == null || !Number.isFinite(rl)) return null;
  if (rl >= 4) return "strong";
  if (rl >= 2) return "moderate";
  return "poor";
};

const isStrongRl = (rl: number | null | undefined): boolean =>
  getRlStrengthBand(rl) === "strong";

const ATTENTION_COLUMNS: ReadonlyArray<{
  id: AttentionBucket;
  label: string;
  titleClassName: string;
}> = [
  {
    id: "unassessed",
    label: "Unassessed",
    titleClassName: "text-gray-900",
  },
  {
    id: "no_goal_set",
    label: "No Goal Set",
    titleClassName: "text-gray-900",
  },
  {
    id: "low_score",
    label: "Low Score",
    titleClassName: "text-gray-900",
  },
];

const CARD_GRID =
  "grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3";

const relationshipPanelShellClass =
  "mb-2 flex h-[calc(100vh-22rem)] min-h-[280px] flex-col overflow-hidden rounded-xl";

const relationshipPanelHeaderClass =
  "flex shrink-0 flex-wrap items-end justify-between gap-x-3 gap-y-2 bg-white mb-4 border-b";

const relationshipPanelBodyClass = "flex min-h-0 flex-1 flex-col bg-gray-50";

const pairCardClass =
  "flex cursor-pointer flex-col gap-3.5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:border-gray-300 hover:shadow-md";

/** Horizontal attention-row cards — slightly wider for scroll rows. */
const pairCardHorizontalWidthClass =
  "w-[min(100%,400px)] min-w-[340px] shrink-0";

const emptyPairCardClass =
  "flex min-h-[210px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-4";

const CHIP_BASE =
  "cursor-pointer rounded-full border px-3 py-1 text-xs font-bold transition-colors select-none";

/** Shared 3-col track so footer RL / Assessed / action line up. */
const pairCardCols =
  "grid grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(5.75rem,auto)] gap-x-4";

/** Account + Renewal row under the pair header. */
const pairMetaCols = "grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-x-4";

/** Thin gray scrollbar always visible — matches portfolio / synopsis scroll style. */
const columnScrollClass =
  "flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto pb-2 pr-1.5 [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300";

const formatLowScoreSummary = (pair: RelationshipPairRow): string | null => {
  const parts: string[] = [];
  for (const trigger of pair.low_score_triggers) {
    if (trigger === "renewal_proximity" && pair.account_renewal_days != null) {
      parts.push(`renewal in ${pair.account_renewal_days}d`);
    } else if (trigger === "rl_gap_severity" && pair.rl_gap != null) {
      parts.push(`${pair.rl_gap}-level gap`);
    } else if (
      trigger === "assessment_stale" &&
      pair.assessed_days_ago != null
    ) {
      parts.push(`${pair.assessed_days_ago}d stale`);
    } else if (TRIGGER_LABELS[trigger]) {
      parts.push(TRIGGER_LABELS[trigger]);
    }
  }
  return parts.length ? parts.join(" · ") : null;
};

const detailsActionBtnBase =
  "inline-flex h-8 shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md border px-2.5 text-[11px] font-medium leading-none transition";

/** mock: .ra-btn (amber, unassessed/low_score "Assess") vs .set-goal-btn
 * (brand-purple, no_goal_set "Set goal") vs plain gray "Open" otherwise. */
const getPairActionBtnClass = (bucket: AttentionBucket) => {
  if (bucket === "unassessed" || bucket === "low_score") {
    return `${detailsActionBtnBase} border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100`;
  }
  if (bucket === "no_goal_set") {
    return `${detailsActionBtnBase} border-brand bg-brand-faint text-brand hover:bg-brand hover:text-white`;
  }
  return `${detailsActionBtnBase} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
};

/** mock: healthy-panel cards at goal show green "RL5 ✓ At goal"
 * (`.pcv2-meta-val` inline `style="color:var(--green)"`, literal " ✓ At
 * goal" suffix) instead of the "current → goal" arrow format. */
const formatRlWithGoal = (
  rlCurrent: number | null | undefined,
  rlGoal: number | null | undefined
): ReactNode => {
  if (rlCurrent == null) {
    return <span title="Unassessed">RL ?</span>;
  }
  if (rlGoal == null) return `RL ${rlCurrent}`;
  if (rlCurrent >= rlGoal) {
    return (
      <span className="font-semibold text-green-600">
        RL {rlCurrent} ✓ At goal
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-baseline gap-0.5"
      title={`RL ${rlCurrent} → ${rlGoal}`}
    >
      <span className="font-semibold text-gray-800">RL {rlCurrent}</span>
      <span className="font-normal text-gray-400">→</span>
      <span className="font-normal text-gray-400">{rlGoal}</span>
    </span>
  );
};

const StatCell = ({
  label,
  value,
  sub,
  align = "left",
  valueClassName = "text-gray-800",
  subClassName = "text-gray-500",
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  align?: "left" | "right";
  valueClassName?: string;
  subClassName?: string;
}) => (
  <div className={`min-w-0 ${align === "right" ? "text-right" : ""}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
      {label}
    </div>
    <div
      className={`mt-1 truncate text-[13px] font-medium leading-tight ${valueClassName}`}
    >
      {value}
    </div>
    {sub ? (
      <div
        className={`mt-0.5 truncate text-[11px] font-normal leading-tight ${subClassName}`}
      >
        {sub}
      </div>
    ) : null}
  </div>
);

const GENERIC_CONTACT_TITLES = new Set([
  "customer contact",
  "contact",
  "unknown",
]);

const buildPairContactDisplay = (
  contactName: string,
  contactTitle?: string | null
): {
  contactLabel: string;
  jobTitle: string | null;
} => {
  const name = String(contactName || "").trim() || "Contact";
  const rawTitle = String(contactTitle || "").trim();
  const jobTitle =
    rawTitle && !GENERIC_CONTACT_TITLES.has(rawTitle.toLowerCase())
      ? rawTitle
      : null;
  return { contactLabel: name, jobTitle };
};

const PairCardHeader = ({
  sponsorName,
  contactName,
  contactTitle,
  strengthMeta,
  lowScoreSummary,
  scope,
}: {
  sponsorName: string;
  contactName: string;
  contactTitle?: string | null;
  strengthMeta: (typeof RL_STRENGTH_META)[RlStrengthBand] | null;
  lowScoreSummary: string | null;
  scope: Scope;
}) => {
  const showSponsor = scope === "team";
  const { contactLabel, jobTitle } = buildPairContactDisplay(
    contactName,
    contactTitle
  );
  const lineTitle = [showSponsor ? sponsorName : null, contactLabel, jobTitle]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="border-b border-gray-100 pb-3">
      <div className="flex items-center gap-2.5">
        <div
          className="flex min-w-0 flex-1 items-center gap-1 truncate text-[13px] leading-tight"
          title={lineTitle}
        >
          {showSponsor ? (
            <span className="max-w-[34%] shrink-0 truncate font-semibold text-gray-900">
              {sponsorName}
            </span>
          ) : null}
          <ArrowLeftRight
            size={11}
            strokeWidth={2}
            className="shrink-0 text-gray-500"
            aria-hidden
          />
          <span className="min-w-0 truncate">
            <span className="font-semibold text-gray-900">{contactLabel}</span>
            {jobTitle ? (
              <>
                <span className="mx-1 font-normal text-gray-400">·</span>
                <span className="font-normal text-gray-500">{jobTitle}</span>
              </>
            ) : null}
          </span>
        </div>
        {(strengthMeta || lowScoreSummary) && (
          <div className="flex shrink-0 items-center gap-1">
            {strengthMeta ? (
              <span
                className={`inline-flex shrink-0 items-center whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold leading-none ${strengthMeta.chipClass}`}
                title={`${strengthMeta.label} (${strengthMeta.range})`}
              >
                {strengthMeta.label}
              </span>
            ) : null}
            {lowScoreSummary ? (
              <span
                className="inline-flex max-w-[9rem] items-center truncate rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-800"
                title={lowScoreSummary}
              >
                {lowScoreSummary}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const TRIGGER_LABELS: Record<string, string> = {
  renewal_proximity: "renewal approaching with RL below goal",
  rl_gap_severity: "RL gap from goal",
  assessment_stale: "stale assessment",
  signal_recency: "no recent interaction signal",
};

interface Props {
  pairs: RelationshipPairRow[];
  accounts?: PortfolioAccountRow[];
  loading: boolean;
  lowScoreCriteria?: LowScoreCriteria;
  withOrg?: (path: string) => string;
  headerFiltersRef?: RefObject<HTMLDivElement | null>;
  workingSetAccountIds?: string[] | null;
  onClearWorkingSet?: () => void;
  /** One-shot deep-link: apply Account filter then clear. */
  focusAccountName?: string | null;
  focusAccountId?: string | null;
  /** When true, apply focus filter only — pairs modal already shown on Account Priorities. */
  suppressPairsFocusModal?: boolean;
  onFocusConsumed?: () => void;
  /** Return to Account Priorities (Pairs deep-link Go back). */
  onGoBackToAccountPriorities?: () => void;
}

type RlModalEntity = Record<string, unknown>;

type AssessmentTarget = {
  pair: RlModalEntity;
  stakeholder: RlModalEntity;
  contact: RlModalEntity;
};

/** Build RlAssessmentModal entities from a portfolio relationship pair row. */
const buildAssessmentTarget = (
  row: RelationshipPairRow
): AssessmentTarget | null => {
  const accountId = String(row.account_id || "").trim();
  const contactId = String(row.contact_id || "").trim();
  const sponsorId = String(row.sponsor_id || "").trim();
  if (!accountId || !contactId) return null;

  const stakeholder: RlModalEntity = {
    id: sponsorId ? `team-${sponsorId}` : "team-current-user",
    sourceId: sponsorId || null,
    accountId,
    name: row.sponsor_name || "You",
    title: "Account team",
    ladderLevel: 2,
    vendorType: "team_member",
  };

  const contact: RlModalEntity = {
    id: contactId,
    accountId,
    name: row.contact_name || "Customer Contact",
    title: row.contact_title || "Customer Contact",
  };

  const pair: RlModalEntity = {
    id:
      sponsorId && contactId
        ? `${sponsorId}::${contactId}`
        : String(row.pair_id),
    accountId,
    contactId,
    vendorSourceId: sponsorId || null,
    vendorStakeholderId: stakeholder.id,
    rlLevel: row.rl_current,
    goalTargetRl: row.rl_goal,
    assessedPosture: null,
    rationale: null,
    goalTargetHorizon: null,
    raw: {
      mapping: { user_id: sponsorId || null },
      contact: { user_id: contactId, id: contactId },
      executive_id: sponsorId || null,
    },
  };

  return { pair, stakeholder, contact };
};

const CriterionRow = ({
  active,
  label,
  detail,
}: {
  active: boolean;
  label: string;
  detail?: string;
}) => (
  <div
    className={`mb-2 flex items-start gap-2 text-[12px] last:mb-0 ${
      active ? "text-gray-800" : "text-gray-400"
    }`}
  >
    <span
      className={`mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm text-[9px] font-bold ${
        active ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-400"
      }`}
    >
      {active ? "✓" : "–"}
    </span>
    <div>
      <div className="font-semibold leading-snug">{label}</div>
      {detail ? (
        <div className="mt-0.5 text-[11px] font-normal text-gray-400">
          {detail}
        </div>
      ) : null}
    </div>
  </div>
);

const formatRenewalMonthDayYear = (
  raw?: string | Date | null
): string | null => {
  if (raw == null || raw === "") return null;
  let date: Date;
  if (raw instanceof Date) {
    date = raw;
  } else {
    const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    date = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(raw);
  }
  if (Number.isNaN(date.getTime())) return null;
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const dateFromRenewalDays = (renewalDays: number): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + renewalDays);
  return date;
};

const formatRenewalParts = (
  renewalDate?: string | Date | null,
  renewalDays?: number | null
): { primary: string; secondary?: string; urgent: boolean } => {
  const dateLabel =
    formatRenewalMonthDayYear(renewalDate) ||
    (renewalDays != null
      ? formatRenewalMonthDayYear(dateFromRenewalDays(renewalDays))
      : null);
  const urgent = renewalDays != null && renewalDays < 0;
  const daysLabel =
    renewalDays == null
      ? null
      : urgent
        ? `Due: ${renewalDays} Days`
        : `${renewalDays} Days remaining`;
  if (dateLabel != null) {
    return { primary: dateLabel, secondary: daysLabel ?? undefined, urgent };
  }
  if (daysLabel != null) {
    return { primary: daysLabel, urgent };
  }
  return { primary: "—", urgent: false };
};

const RelationshipPrioritiesTabContent = ({
  pairs,
  accounts = [],
  loading,
  lowScoreCriteria,
  withOrg = (path: string) => path,
  headerFiltersRef,
  workingSetAccountIds,
  onClearWorkingSet,
  focusAccountName = null,
  focusAccountId = null,
  suppressPairsFocusModal = false,
  onFocusConsumed,
  onGoBackToAccountPriorities,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const executiveId =
    loggedInUser?.executive_id ||
    loggedInUser?.executiveId ||
    loggedInUser?.user_id ||
    loggedInUser?.userId ||
    loggedInUser?.id ||
    null;
  const canOpenSettings =
    isRole(loggedInUser?.role, ROLES.VENDOR_ADMIN) ||
    isRole(loggedInUser?.role, ROLES.EXECUTIVE_SPONSOR_LEAD);
  const criteria = useMemo(
    () =>
      normalizeLowScoreCriteria(
        parseLowScoreCriteria(lowScoreCriteria) ?? DEFAULT_LOW_SCORE_CRITERIA
      ),
    [lowScoreCriteria]
  );
  const [relTab, setRelTab] = useState<RelTab>("attn");
  const [attnFilter, setAttnFilter] = useState<AttnFilter>("all");
  const [attnLayout, setAttnLayout] = useState<AttnLayout>("columns");
  const [searchQuery, setSearchQuery] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [scope, setScope] = useState<Scope>("mine");
  const [lsOpen, setLsOpen] = useState(false);
  const [assessmentTarget, setAssessmentTarget] =
    useState<AssessmentTarget | null>(null);
  const [viewMode, setViewMode] = useState<"health" | "prioritized">("health");
  const [pfmConfirmOpen, setPfmConfirmOpen] = useState(false);
  const [prioritizedAt, setPrioritizedAt] = useState<Date | null>(null);
  const [pairsFocusModal, setPairsFocusModal] =
    useState<PairsFocusModalKind | null>(null);
  const [pairsFocusAccountId, setPairsFocusAccountId] = useState<string | null>(
    null
  );
  const [pairsFocusAccountLabel, setPairsFocusAccountLabel] = useState("");
  const lsRef = useRef<HTMLDivElement>(null);
  const lsCloseTimerRef = useRef<number | null>(null);

  const cancelLsClose = () => {
    if (lsCloseTimerRef.current != null) {
      window.clearTimeout(lsCloseTimerRef.current);
      lsCloseTimerRef.current = null;
    }
  };

  const openLsPopover = () => {
    cancelLsClose();
    setLsOpen(true);
  };

  const scheduleLsClose = () => {
    cancelLsClose();
    lsCloseTimerRef.current = window.setTimeout(() => {
      setLsOpen(false);
      lsCloseTimerRef.current = null;
    }, 160);
  };

  useEffect(() => {
    return () => cancelLsClose();
  }, []);

  useEffect(() => {
    // Wait for overview pairs so we don't flash a false "no mappings" modal.
    if (loading) return;

    const name = (focusAccountName || "").trim();
    const accountId = (focusAccountId || "").trim();
    if (!name && !accountId) return;

    const label =
      name ||
      accounts.find(a => String(a.customer_id) === accountId)?.customer_name ||
      accountId;
    if (label) setAccountFilter(label);

    // Evaluate against all portfolio pairs for this account (not scope-limited),
    // matching Account Priorities Pairs counts which are account-level.
    const accountPairs = pairs.filter(p => {
      const pairAccountId = String(p.account_id || "").trim();
      const pairName = (p.account_name || "").trim();
      if (accountId && pairAccountId === accountId) return true;
      if (name && pairName === name) return true;
      return false;
    });
    const attentionCount = accountPairs.filter(p =>
      ATTENTION_BUCKETS.includes(p.attention_bucket as AttentionBucket)
    ).length;

    const resolvedId =
      accountId ||
      (accountPairs[0] ? String(accountPairs[0].account_id) : "") ||
      null;

    setPairsFocusAccountId(resolvedId);
    setPairsFocusAccountLabel(label);

    if (accountPairs.length === 0) {
      // Modal already shown on Account Priorities when suppressPairsFocusModal.
      setPairsFocusModal(suppressPairsFocusModal ? null : "no_mappings");
    } else if (attentionCount === 0) {
      if (!suppressPairsFocusModal) setPairsFocusModal("all_stable");
      else setPairsFocusModal(null);
      // Pairs exist outside Needs Attention — show Healthy so the list isn't empty.
      setRelTab("healthy");
    } else {
      setPairsFocusModal(null);
      setRelTab("attn");
    }

    onFocusConsumed?.();
  }, [
    focusAccountName,
    focusAccountId,
    suppressPairsFocusModal,
    onFocusConsumed,
    pairs,
    accounts,
    loading,
  ]);

  useEffect(() => {
    if (!lsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (lsRef.current && !lsRef.current.contains(e.target as Node)) {
        setLsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [lsOpen]);

  const accountOptions = useMemo(() => {
    const names = new Set<string>();
    for (const account of accounts) {
      const name = (account.customer_name || "").trim();
      if (name) names.add(name);
    }
    // Fallback if overview accounts are not loaded yet.
    if (names.size === 0) {
      for (const pair of pairs) {
        const name = (pair.account_name || "").trim();
        if (name) names.add(name);
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [accounts, pairs]);

  const renewalDateByAccount = useMemo(() => {
    const map = new Map<string, string>();
    for (const account of accounts) {
      if (account.renewal_date) {
        map.set(account.customer_id, account.renewal_date);
      }
    }
    return map;
  }, [accounts]);

  const scopedPairs = useMemo(() => {
    let list = pairs;
    if (scope === "mine") list = list.filter(p => p.is_mine);
    if (workingSetAccountIds) {
      const ids = new Set(workingSetAccountIds);
      list = list.filter(p => ids.has(String(p.account_id)));
    }
    return list;
  }, [pairs, scope, workingSetAccountIds]);

  const needsAttentionPairs = useMemo(
    () =>
      scopedPairs.filter(p =>
        ATTENTION_BUCKETS.includes(p.attention_bucket as AttentionBucket)
      ),
    [scopedPairs]
  );

  const closePairsFocusModal = useCallback(() => {
    setPairsFocusModal(null);
  }, []);

  const handlePairsFocusGoBack = useCallback(() => {
    setPairsFocusModal(null);
    setAccountFilter("");
    onGoBackToAccountPriorities?.();
  }, [onGoBackToAccountPriorities]);

  const handlePairsFocusSeeAllRelationships = useCallback(() => {
    setPairsFocusModal(null);
    const params = new URLSearchParams({ tab: "account-relationships" });
    if (pairsFocusAccountId) {
      params.set("accountId", pairsFocusAccountId);
    }
    navigate(withOrg(`/relationship?${params.toString()}`));
  }, [navigate, withOrg, pairsFocusAccountId]);

  const healthyPairs = useMemo(
    () =>
      scopedPairs.filter(p => isStrongRl(p.rl_current) && p.rl_goal != null),
    [scopedPairs]
  );

  const baseList = relTab === "attn" ? needsAttentionPairs : healthyPairs;

  const filtered = useMemo(() => {
    let list = baseList;
    if (accountFilter) {
      list = list.filter(p => p.account_name === accountFilter);
    }
    if (relTab === "attn" && attnFilter !== "all") {
      list = list.filter(p => p.attention_bucket === attnFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        p =>
          p.contact_name.toLowerCase().includes(q) ||
          p.account_name.toLowerCase().includes(q) ||
          (p.contact_title || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [baseList, accountFilter, attnFilter, searchQuery, relTab]);

  const groupedByBucket = useMemo(() => {
    if (relTab !== "attn" || attnFilter !== "all") return { all: filtered };
    const map: Record<string, RelationshipPairRow[]> = {
      unassessed: [],
      no_goal_set: [],
      low_score: [],
    };
    filtered.forEach(p => {
      const b = p.attention_bucket;
      if (map[b]) map[b].push(p);
    });
    return map;
  }, [filtered, relTab, attnFilter]);

  const attnChipCounts = useMemo(() => {
    let list = needsAttentionPairs;
    if (accountFilter) {
      list = list.filter(p => p.account_name === accountFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        p =>
          p.contact_name.toLowerCase().includes(q) ||
          p.account_name.toLowerCase().includes(q) ||
          (p.contact_title || "").toLowerCase().includes(q)
      );
    }
    const counts: Record<string, number> = {
      all: list.length,
      unassessed: 0,
      no_goal_set: 0,
      low_score: 0,
    };
    for (const p of list) {
      const b = p.attention_bucket;
      if (typeof counts[b] === "number") counts[b] += 1;
    }
    return counts;
  }, [needsAttentionPairs, accountFilter, searchQuery]);

  const relTabs = useMemo(
    () => [
      {
        id: "attn",
        label: "Needs Attention",
        count: needsAttentionPairs.length,
        icon: <AlertTriangle className="h-3.5 w-3.5" aria-hidden />,
      },
      {
        id: "healthy",
        label: "Healthy",
        count: healthyPairs.length,
        icon: <CircleCheckBig className="h-3.5 w-3.5" aria-hidden />,
      },
    ],
    [needsAttentionPairs.length, healthyPairs.length]
  );

  const scopeTabs = useMemo(
    () => [
      { id: "mine", label: "My Relationships" },
      { id: "team", label: "Full Team" },
    ],
    []
  );

  const accountSwitcherOptions = useMemo(
    () => [
      { value: "", label: "All Accounts" },
      ...accountOptions.map(name => ({ value: name, label: name })),
    ],
    [accountOptions]
  );

  const goToAccountRelationships = useCallback(
    (pair: RelationshipPairRow) => {
      const params = new URLSearchParams({
        tab: "account-relationships",
        accountId: String(pair.account_id),
      });
      navigate(withOrg(`/relationship?${params.toString()}`), {
        state: {
          focusVendorId: pair.sponsor_id || null,
          focusContactId: pair.contact_id || null,
        },
      });
    },
    [navigate, withOrg]
  );

  const openAssessModal = useCallback(
    (row: RelationshipPairRow) => {
      const target = buildAssessmentTarget(row);
      if (!target) {
        goToAccountRelationships(row);
        return;
      }
      setAssessmentTarget(target);
    },
    [goToAccountRelationships]
  );

  const handleAssessmentSubmitted = useCallback(async () => {
    setAssessmentTarget(null);
    await dispatch(fetchMyPortfolioOverview());
  }, [dispatch]);

  const renderPairCard = (pair: RelationshipPairRow) => {
    const renewal = formatRenewalParts(
      pair.account_renewal_date ||
        renewalDateByAccount.get(pair.account_id) ||
        null,
      pair.account_renewal_days
    );

    const sponsorName =
      pair.sponsor_name ||
      loggedInUser?.name ||
      loggedInUser?.full_name ||
      "You";
    const lowScoreSummary = formatLowScoreSummary(pair);

    const assessedText =
      pair.assessed_days_ago != null
        ? pair.assessed_days_ago === 0
          ? "Today"
          : `${pair.assessed_days_ago}d ago`
        : "-";
    const isUnassessed = pair.attention_bucket === "unassessed";
    const mappedText =
      pair.mapped_days_ago != null
        ? pair.mapped_days_ago === 0
          ? "Today"
          : `${pair.mapped_days_ago}d ago`
        : "-";
    const timingLabel = isUnassessed ? "Mapped" : "Assessed";
    const timingValue = isUnassessed ? mappedText : assessedText;

    const strengthBand = getRlStrengthBand(pair.rl_current);
    const strengthMeta = strengthBand ? RL_STRENGTH_META[strengthBand] : null;

    const actionLabel =
      pair.attention_bucket === "unassessed" ||
      pair.attention_bucket === "low_score"
        ? "Assess"
        : pair.attention_bucket === "no_goal_set"
          ? "Set Goal"
          : "Open";

    return (
      <div
        key={pair.pair_id}
        className={pairCardClass}
        onClick={() => goToAccountRelationships(pair)}
        onKeyDown={e => {
          if (e.key === "Enter") goToAccountRelationships(pair);
        }}
        role="button"
        tabIndex={0}
      >
        <PairCardHeader
          sponsorName={sponsorName}
          contactName={pair.contact_name}
          contactTitle={pair.contact_title}
          strengthMeta={strengthMeta}
          lowScoreSummary={lowScoreSummary}
          scope={scope}
        />

        <div className={`${pairMetaCols} items-start pt-0.5`}>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Account
            </div>
            <div className="mt-1 flex min-w-0 items-center gap-2">
              <span
                className="inline-flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-[5px] text-[10px] font-bold"
                style={
                  getAccountIdentityBadgeStyle(pair.account_id, {
                    borderWidth: 1.25,
                    color: ACCOUNT_IDENTITY_FALLBACK,
                  }) as CSSProperties
                }
                aria-hidden
              >
                {(getInitials(pair.account_name) || "?").slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold leading-tight text-gray-900">
                  {pair.account_name}
                </div>
                {pair.account_region ? (
                  <div className="mt-0.5 truncate text-[11px] leading-tight text-gray-500">
                    {pair.account_region}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <StatCell
            label="Renewal"
            value={renewal.primary}
            sub={renewal.secondary}
            align="right"
            valueClassName="text-gray-800"
            subClassName={renewal.urgent ? "text-slate-600" : "text-gray-400"}
          />
        </div>

        <div
          className={`${pairCardCols} items-center rounded-lg bg-gray-50 px-3 py-2.5`}
        >
          <StatCell
            label="RL"
            value={formatRlWithGoal(pair.rl_current, pair.rl_goal)}
            valueClassName={
              pair.rl_current != null ? "text-gray-800" : "text-gray-500"
            }
          />
          <StatCell
            label={timingLabel}
            value={timingValue}
            valueClassName={
              timingValue === "-" ? "text-gray-400" : "text-gray-800"
            }
          />
          <div className="flex shrink-0 items-center justify-end self-center">
            <button
              type="button"
              className={getPairActionBtnClass(
                pair.attention_bucket as AttentionBucket
              )}
              onClick={e => {
                e.stopPropagation();
                if (
                  pair.attention_bucket === "unassessed" ||
                  pair.attention_bucket === "no_goal_set" ||
                  pair.attention_bucket === "low_score"
                ) {
                  openAssessModal(pair);
                  return;
                }
                goToAccountRelationships(pair);
              }}
            >
              {actionLabel}
              <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAttentionColumns = () => (
    <div className="grid h-full min-h-0 grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-0 sm:divide-x sm:divide-gray-200 xl:grid-cols-3">
      {ATTENTION_COLUMNS.map(column => {
        const items = groupedByBucket[column.id] || [];
        return (
          <div
            key={column.id}
            className="flex h-full min-h-0 min-w-0 flex-col sm:px-2.5 first:sm:pl-0 last:sm:pr-0"
          >
            <div
              className={`relative mb-3.5 flex shrink-0 items-center text-[13px] font-semibold ${column.titleClassName}`}
            >
              {column.label}
            </div>
            <div className={columnScrollClass}>
              {items.length > 0 ? (
                items.map(renderPairCard)
              ) : (
                <div className={emptyPairCardClass}>
                  <div className="mb-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <Users size={16} strokeWidth={1.75} />
                  </div>
                  <p className="m-0 text-xs font-medium text-gray-500">
                    No pairs
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderAttentionRows = () => (
    <div className="flex flex-col gap-6">
      {ATTENTION_COLUMNS.map(column => {
        const items = groupedByBucket[column.id] || [];
        return (
          <div
            key={column.id}
            className="flex shrink-0 flex-col gap-3.5 border-b border-gray-200 last:border-b-0 last:pb-0"
          >
            <div
              className={`relative flex shrink-0 items-center text-[13px] font-semibold ${column.titleClassName}`}
            >
              {column.label}
            </div>
            {items.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto pt-0.5 pb-2.5 [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300">
                {items.map(pair => (
                  <div
                    key={pair.pair_id}
                    className={pairCardHorizontalWidthClass}
                  >
                    {renderPairCard(pair)}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`${emptyPairCardClass} ${pairCardHorizontalWidthClass}`}
              >
                <div className="mb-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <Users size={16} strokeWidth={1.75} />
                </div>
                <p className="m-0 text-xs font-medium text-gray-500">
                  No pairs
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const showAttentionBoard = relTab === "attn" && attnFilter === "all";
  const showAttentionRows = showAttentionBoard && attnLayout === "rows";
  // const showAttentionColumns = showAttentionBoard;

  const pairGrid = showAttentionBoard ? (
    attnLayout === "rows" ? (
      renderAttentionRows()
    ) : (
      renderAttentionColumns()
    )
  ) : (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className={CARD_GRID}>{filtered.map(renderPairCard)}</div>
    </div>
  );

  const headerFilters = headerFiltersRef?.current
    ? createPortal(
        <div className="flex flex-nowrap items-center justify-end gap-2 overflow-visible">
          <div className="flex items-center self-center">
            <TabsComponent
              tabs={scopeTabs}
              activeTab={scope}
              onTabChange={id => setScope(id as Scope)}
              variant="secondary-segmented"
              segmentedLayout="fit"
              className="mb-0 h-10 shrink-0 whitespace-nowrap rounded-full !p-0.5 [&_button]:!px-3 [&_button]:!py-1.5 [&_button]:!text-[13px]"
            />
          </div>
          <div className="w-[180px] shrink-0 self-center">
            <SearchInput
              placeholder="Search contact…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              rounded="normal"
              size="sm"
            />
          </div>
          <DropdownSwitcher
            className="w-[200px] shrink-0 self-center"
            value={accountFilter}
            prefix="Account: "
            options={accountSwitcherOptions}
            onChange={setAccountFilter}
            ariaLabel={`Account: ${
              accountSwitcherOptions.find(o => o.value === accountFilter)
                ?.label ?? "All Accounts"
            }. Change account filter.`}
            listboxLabel="Account"
          />
        </div>,
        headerFiltersRef.current
      )
    : null;

  // Residual deep-link modal (Account Priorities now shows this first).
  const pairsFocusModalNode = (
    <CommonModal
      isOpen={pairsFocusModal != null}
      toggle={closePairsFocusModal}
      onCloseButton={closePairsFocusModal}
      size="xl"
      usePortal
      title={
        pairsFocusModal === "no_mappings"
          ? "No relationship mappings found"
          : "Relationships look stable"
      }
      footer={
        <div className="flex w-full flex-nowrap items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
            onClick={closePairsFocusModal}
          >
            Stay here
          </button>
          <button
            type="button"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-[12.5px] font-semibold text-purple-700 transition-colors hover:border-purple-300 hover:bg-purple-100"
            onClick={handlePairsFocusGoBack}
          >
            Go to Account Priorities
          </button>
          <button
            type="button"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-purple-600 bg-purple-600 px-3 py-2 text-[12.5px] font-semibold text-white transition-colors hover:border-purple-700 hover:bg-purple-700"
            onClick={handlePairsFocusSeeAllRelationships}
          >
            See all Relationships
            <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
          </button>
        </div>
      }
    >
      {pairsFocusModal === "no_mappings" ? (
        <p className="m-0 text-[13.5px] leading-relaxed text-gray-600">
          {pairsFocusAccountLabel ? (
            <>
              There are no mapped relationship pairs for{" "}
              <span className="font-semibold text-gray-800">
                {pairsFocusAccountLabel}
              </span>{" "}
              yet. Map contacts in Account Relationships to start tracking them
              here, or return to Account Priorities.
            </>
          ) : (
            <>
              There are no mapped relationship pairs for this account yet. Map
              contacts in Account Relationships to start tracking them here, or
              return to Account Priorities.
            </>
          )}
        </p>
      ) : (
        <p className="m-0 text-[13.5px] leading-relaxed text-gray-600">
          {pairsFocusAccountLabel ? (
            <>
              Mapped pairs for{" "}
              <span className="font-semibold text-gray-800">
                {pairsFocusAccountLabel}
              </span>{" "}
              do not fall under Needs Attention (Unassessed, No Goal Set, or Low
              Score). They currently fall under{" "}
              <span className="font-semibold text-gray-800">Stable</span>{" "}
              relationships. Open Account Relationships to review them, or
              return to Account Priorities.
            </>
          ) : (
            <>
              Mapped pairs for this account do not fall under Needs Attention
              (Unassessed, No Goal Set, or Low Score). They currently fall under{" "}
              <span className="font-semibold text-gray-800">Stable</span>{" "}
              relationships. Open Account Relationships to review them, or
              return to Account Priorities.
            </>
          )}
        </p>
      )}
    </CommonModal>
  );

  if (loading) {
    return (
      <div>
        {headerFilters}
        <div className="flex min-h-[320px] flex-col items-center justify-center text-gray-500">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600" />
          <p className="mt-2">Loading relationships...</p>
        </div>
        {pairsFocusModalNode}
      </div>
    );
  }

  if (pairs.length === 0) {
    return (
      <div>
        {headerFilters}
        <div className="flex min-h-[320px] flex-col items-center justify-center text-gray-500">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <Users size={18} strokeWidth={1.75} aria-hidden />
          </div>
          <p className="text-lg font-medium text-gray-700">
            No relationship pairs found
          </p>
          <p className="mt-1 text-sm">
            Map contacts to accounts in Account Workspace to see them here.
          </p>
        </div>
        {pairsFocusModalNode}
      </div>
    );
  }

  const emptyFilterLabel =
    attnFilter === "unassessed"
      ? "Unassessed"
      : attnFilter === "no_goal_set"
        ? "No Goal Set"
        : attnFilter === "low_score"
          ? "Low Score"
          : null;

  const emptyStatePanel = (
    <div className="flex h-full min-h-[280px] flex-col items-center justify-center px-4 py-8 sm:px-6">
      {relTab === "attn" &&
      needsAttentionPairs.length === 0 &&
      attnFilter === "all" &&
      !searchQuery &&
      !accountFilter ? (
        <div className="flex w-full max-w-lg flex-col items-center rounded-xl border border-dashed border-gray-300 bg-white px-8 py-12 text-center">
          <div className="mb-3.5 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <CircleCheckBig size={22} strokeWidth={1.75} aria-hidden />
          </div>
          <p className="m-0 text-[15px] font-semibold text-gray-800">
            No relationships need attention right now
          </p>
          <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-gray-500">
            That&apos;s a positive outcome — review Healthy relationships or
            check Relationship Strengths for momentum opportunities.
          </p>
        </div>
      ) : (
        <div className="flex w-full max-w-md flex-col items-center rounded-xl border border-dashed border-gray-300 bg-white px-8 py-12 text-center">
          <div className="mb-3.5 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <Users size={22} strokeWidth={1.75} aria-hidden />
          </div>
          <p className="m-0 text-[15px] font-semibold text-gray-800">
            {emptyFilterLabel
              ? `No pairs in ${emptyFilterLabel}`
              : "No pairs match your filters"}
          </p>
          <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-gray-500">
            {emptyFilterLabel
              ? "There are no relationship pairs in this category right now. Try another filter or view all pairs."
              : "Try clearing search or filters to see more relationships."}
          </p>
          {(attnFilter !== "all" || searchQuery || accountFilter) && (
            <button
              type="button"
              className="mt-5 inline-flex cursor-pointer items-center justify-center rounded-full border border-purple-200 bg-purple-50 px-4 py-1.5 text-[12px] font-semibold text-purple-700 transition-colors hover:border-purple-300 hover:bg-purple-100"
              onClick={() => {
                setAttnFilter("all");
                setSearchQuery("");
                setAccountFilter("");
              }}
            >
              Show all pairs
            </button>
          )}
        </div>
      )}
    </div>
  );

  // const handleOpenPfm = () => {
  //   if (viewMode === "prioritized") {
  //     setPfmConfirmOpen(true);
  //     return;
  //   }
  //   setPfmConfirmOpen(true);
  // };
  const handleConfirmPfm = () => {
    setPfmConfirmOpen(false);
    setPrioritizedAt(new Date());
    setViewMode("prioritized");
  };

  return (
    <div>
      {headerFilters}

      {workingSetAccountIds && onClearWorkingSet && (
        <WorkingSetBanner
          count={workingSetAccountIds.length}
          onClear={onClearWorkingSet}
        />
      )}

      {/* Prioritize for Me — temporarily hidden; handlers/state/modal kept for re-enable.
      {viewMode === "health" && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={handleOpenPfm}
            className="ml-auto rounded-[7px] border-none bg-brand px-[18px] py-2 text-[12.5px] font-bold text-white hover:bg-brand-dark"
          >
            {prioritizedAt ? "↺ Re-prioritize" : "Prioritize for Me"}
          </button>
        </div>
      )}
      */}

      <PfmConfirmModal
        isOpen={pfmConfirmOpen}
        onCancel={() => setPfmConfirmOpen(false)}
        onConfirm={handleConfirmPfm}
      />

      {viewMode === "prioritized" ? (
        <RelationshipPrioritizedView
          pairs={pairs}
          onBackToHealth={() => setViewMode("health")}
          prioritizedAt={prioritizedAt}
        />
      ) : (
        <>
          <div className={relationshipPanelShellClass}>
            <div className={relationshipPanelHeaderClass}>
              <TabsComponent
                tabs={relTabs}
                activeTab={relTab}
                onTabChange={id => {
                  setRelTab(id as RelTab);
                  if (id !== "attn") setAttnFilter("all");
                }}
                variant="tab-underline"
                className="-mb-px mb-0 [&_.tab-item]:!px-2 [&_.tab-item]:!pb-2.5 [&_.tab-item]:!pt-1"
              />

              {relTab === "attn" ? (
                <div className="relative flex flex-wrap items-center gap-1.5 pb-2">
                  {(
                    [
                      ["all", "All"],
                      ["unassessed", "Unassessed"],
                      ["no_goal_set", "No Goal Set"],
                      ["low_score", "Low Score"],
                    ] as const
                  ).map(([id, label]) => {
                    if (id === "all") {
                      const allActive = attnFilter === "all";
                      return (
                        <div
                          key={id}
                          className="inline-flex items-center gap-2.5"
                        >
                          <div
                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-1.5 py-1"
                            role="group"
                            aria-label="Attention board layout"
                          >
                            <button
                              type="button"
                              className={`inline-flex h-5 w-5 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors ${
                                allActive && attnLayout === "columns"
                                  ? "text-purple-600"
                                  : "text-gray-400 hover:text-gray-600"
                              }`}
                              aria-label="Column layout"
                              aria-pressed={
                                allActive && attnLayout === "columns"
                              }
                              title="Columns"
                              onClick={() => {
                                setAttnFilter("all");
                                setAttnLayout("columns");
                              }}
                            >
                              <Columns3
                                className="h-3.5 w-3.5"
                                strokeWidth={2.25}
                                aria-hidden
                              />
                            </button>
                            <span
                              className="h-3 w-px shrink-0 bg-gray-300"
                              aria-hidden
                            />
                            <button
                              type="button"
                              className={`inline-flex h-5 w-5 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors ${
                                allActive && attnLayout === "rows"
                                  ? "text-purple-600"
                                  : "text-gray-400 hover:text-gray-600"
                              }`}
                              aria-label="Row layout"
                              aria-pressed={allActive && attnLayout === "rows"}
                              title="Rows"
                              onClick={() => {
                                setAttnFilter("all");
                                setAttnLayout("rows");
                              }}
                            >
                              <List
                                className="h-3.5 w-3.5"
                                strokeWidth={2.25}
                                aria-hidden
                              />
                            </button>
                          </div>
                          <span
                            className="h-5 w-px shrink-0 self-center bg-gray-300"
                            aria-hidden
                          />
                          <button
                            type="button"
                            className={`${CHIP_BASE} ${
                              allActive
                                ? "border-purple-600 bg-purple-600 text-white"
                                : "border-gray-200 bg-white text-gray-500 hover:border-purple-500 hover:text-purple-600"
                            }`}
                            onClick={() => setAttnFilter(id)}
                          >
                            {label} · {attnChipCounts.all}
                          </button>
                        </div>
                      );
                    }

                    if (id === "low_score") {
                      const lowScoreActive = attnFilter === "low_score";
                      const lowScoreCount = attnChipCounts.low_score;
                      return (
                        <div
                          key={id}
                          ref={lsRef}
                          className="relative inline-flex items-center gap-1"
                        >
                          <button
                            type="button"
                            className={`${CHIP_BASE} ${
                              lowScoreActive
                                ? "border-purple-600 bg-purple-600 text-white"
                                : "border-gray-200 bg-white text-gray-500 hover:border-purple-500 hover:text-purple-600"
                            }`}
                            onClick={() => setAttnFilter(id)}
                          >
                            {label} · {lowScoreCount}
                          </button>
                          <button
                            type="button"
                            className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border-none bg-transparent p-0 text-gray-400 hover:text-purple-600"
                            aria-label="View Low Score criteria"
                            onMouseEnter={openLsPopover}
                            onMouseLeave={scheduleLsClose}
                            onFocus={openLsPopover}
                            onBlur={scheduleLsClose}
                            onClick={e => e.stopPropagation()}
                          >
                            <Info
                              className="h-3 w-3"
                              strokeWidth={2.5}
                              aria-hidden
                            />
                          </button>
                          {lsOpen ? (
                            <div
                              className="absolute left-0 top-[calc(100%+8px)] z-[1200] min-w-[300px] max-w-[340px] rounded-[10px] border border-gray-200 bg-white p-4 text-gray-900 shadow-lg sm:left-auto sm:right-0"
                              onMouseEnter={openLsPopover}
                              onMouseLeave={scheduleLsClose}
                            >
                              <div className="mb-2.5 flex items-center justify-between text-[13px] font-extrabold">
                                Low Score Criteria
                                <button
                                  type="button"
                                  className="cursor-pointer border-none bg-transparent text-gray-500"
                                  onClick={() => setLsOpen(false)}
                                >
                                  ✕
                                </button>
                              </div>
                              <p className="mb-3 text-[11px] leading-snug text-gray-500">
                                A pair is flagged as Low Score when{" "}
                                <strong>any</strong> active rule is met.
                                Configured in Settings.
                              </p>
                              <div className="mb-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                                <CriterionRow
                                  active={criteria.renewal_enabled}
                                  label={
                                    criteria.renewal_enabled
                                      ? `Renewal within ${criteria.renewal_days} days AND RL below goal`
                                      : "Renewal proximity + RL below goal"
                                  }
                                  detail={
                                    criteria.renewal_enabled ? undefined : "Off"
                                  }
                                />
                                <CriterionRow
                                  active={criteria.rl_gap_enabled}
                                  label={
                                    criteria.rl_gap_enabled
                                      ? `RL gap ≥ ${criteria.rl_gap_levels} levels from goal`
                                      : "RL gap severity"
                                  }
                                  detail={
                                    criteria.rl_gap_enabled ? undefined : "Off"
                                  }
                                />
                                <CriterionRow
                                  active={criteria.assessment_stale_enabled}
                                  label={
                                    criteria.assessment_stale_enabled
                                      ? `Assessment older than ${criteria.assessment_stale_days} days`
                                      : "Assessment staleness"
                                  }
                                  detail={
                                    criteria.assessment_stale_enabled
                                      ? undefined
                                      : "Off"
                                  }
                                />
                                <CriterionRow
                                  active={false}
                                  label="Signal recency"
                                  detail="Not available yet — requires CRM/calendar access"
                                />
                              </div>
                              <div className="flex items-center justify-end gap-2">
                                {canOpenSettings ? (
                                  <button
                                    type="button"
                                    className="cursor-pointer border-none bg-transparent p-0 text-[12px] font-semibold text-purple-600 hover:text-purple-800"
                                    onClick={() => {
                                      setLsOpen(false);
                                      navigate(withOrg("/settings"));
                                    }}
                                  >
                                    Settings ↗
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-gray-400">
                                    Set by program admin
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    }

                    return (
                      <button
                        key={id}
                        type="button"
                        className={`${CHIP_BASE} ${
                          attnFilter === id
                            ? "border-purple-600 bg-purple-600 text-white"
                            : "border-gray-200 bg-white text-gray-500 hover:border-purple-500 hover:text-purple-600"
                        }`}
                        onClick={() => setAttnFilter(id)}
                      >
                        {label} · {attnChipCounts[id] ?? 0}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className={relationshipPanelBodyClass}>
              <div
                className={
                  showAttentionRows
                    ? "flex-1 overflow-visible px-2 py-2.5"
                    : "min-h-0 flex-1 overflow-hidden px-4 py-4"
                }
              >
                {filtered.length > 0 ? pairGrid : emptyStatePanel}
              </div>
              <div className="flex shrink-0 items-center justify-between border-t border-gray-200/80 bg-gray-50 px-4 py-2.5 text-[11.5px] text-gray-500">
                <span>
                  Showing {filtered.length} of {baseList.length} pairs
                </span>
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[11.5px] font-semibold text-purple-600 hover:underline"
                  onClick={() =>
                    navigate(withOrg("/relationship?tab=my-relationships"))
                  }
                >
                  See all relationships
                  <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <RlAssessmentModal
        open={Boolean(assessmentTarget)}
        pair={assessmentTarget?.pair}
        stakeholder={assessmentTarget?.stakeholder}
        contact={assessmentTarget?.contact}
        executiveId={executiveId}
        onClose={() => setAssessmentTarget(null)}
        onSubmitted={handleAssessmentSubmitted}
      />

      {pairsFocusModalNode}
    </div>
  );
};

const RelationshipPrioritiesTab = (props: Props) => (
  <RelationshipProviders>
    <RelationshipPrioritiesTabContent {...props} />
  </RelationshipProviders>
);

export default RelationshipPrioritiesTab;
